import dayjs from '@/lib/dayjs'
import { formatNumber } from '@/lib/utils'
import type { Dayjs } from 'dayjs'
import type {
  AvailabilityCurrentSummary,
  AvailabilitySummaryChange,
  AvailabilitySummaryLocation,
} from './availability-changes.types'
import {
  getNextAvailabilityChangeOccurrenceForLocations,
  type AvailabilityChangeFields,
  type AvailabilityChangeOccurrence,
  type AvailabilityLocationRef,
} from './availability-changes.utils'

/**
 * The current summary; its fetcher falls back to `{}` for an empty response, so every helper
 * tolerates a missing `locations`.
 */
type AvailabilitySummaryInput = Partial<AvailabilityCurrentSummary> | null | undefined

type AvailabilitySummaryLocationRef = AvailabilitySummaryLocation['location']

/** A summary change with the location it is active at. */
export interface AvailabilitySummaryLocationChange extends AvailabilitySummaryChange {
  location: AvailabilitySummaryLocationRef
}

/** A summary change with every location it is active at. */
export interface ActiveAvailabilityChange extends AvailabilitySummaryChange {
  locations: AvailabilitySummaryLocationRef[]
}

export type AvailabilityStateVariant = 'muted' | 'success' | 'warning' | 'destructive' | 'secondary'

export interface AvailabilityState {
  label: string
  variant: AvailabilityStateVariant
  description: string
}

export function getSummaryChanges(
  summary: AvailabilitySummaryInput
): AvailabilitySummaryLocationChange[] {
  return (summary?.locations ?? []).flatMap((locationSummary) =>
    (locationSummary?.changes ?? []).map((change) => ({
      ...change,
      location: locationSummary.location,
    }))
  )
}

export function getActiveAvailabilityChanges(
  summary: AvailabilitySummaryInput
): ActiveAvailabilityChange[] {
  const changesById = new Map<number, ActiveAvailabilityChange>()
  const locations = summary?.locations ?? []

  locations.forEach((locationSummary) => {
    const changes = locationSummary?.changes ?? []

    changes.forEach((change) => {
      const changeId = change.id

      if (!changeId) {
        return
      }

      const existingChange: ActiveAvailabilityChange = changesById.get(changeId) ?? {
        ...change,
        locations: [],
      }

      existingChange.locations.push(locationSummary.location)
      changesById.set(changeId, existingChange)
    })
  })

  return [...changesById.values()].sort((left, right) =>
    String(left.title ?? '').localeCompare(String(right.title ?? ''))
  )
}

export function getUpcomingAvailabilityChangeOccurrences(
  changes: AvailabilityChangeFields[] = [],
  now: Dayjs = dayjs(),
  activeLocations: AvailabilityLocationRef[] = [],
  limit = 3
): AvailabilityChangeOccurrence[] {
  return changes
    .map((change) => getNextAvailabilityChangeOccurrenceForLocations(change, now, activeLocations))
    .filter((occurrence): occurrence is AvailabilityChangeOccurrence => occurrence !== null)
    .sort((left, right) => dayjs(left.starts_at).valueOf() - dayjs(right.starts_at).valueOf())
    .slice(0, limit)
}

function changeBlocksPickup(change: AvailabilitySummaryChange): boolean {
  return Boolean(change?.pickup_paused)
}

function changeBlocksDelivery(change: AvailabilitySummaryChange): boolean {
  return Boolean(change?.delivery_paused)
}

export function getAvailabilityState(summary: AvailabilitySummaryInput): AvailabilityState {
  const locations = summary?.locations ?? []
  const changes = getSummaryChanges(summary)
  const locationCount = locations.length
  const affectedLocationIds = new Set(
    locations
      .filter((locationSummary) => locationSummary.changes?.length > 0)
      .map((item) => item.location?.id)
  )

  if (locationCount === 0) {
    return {
      label: 'No active locations',
      variant: 'muted',
      description: 'Add a location before customers can choose pickup or delivery dates.',
    }
  }

  if (changes.length === 0) {
    return {
      label: 'Accepting orders',
      variant: 'success',
      description: 'Pickup and delivery availability is normal right now.',
    }
  }

  const allLocationsClosed =
    affectedLocationIds.size >= locationCount &&
    locations.every((item) => item.changes?.some((change) => change.store_closed))
  const pickupUnavailable =
    affectedLocationIds.size >= locationCount &&
    locations.every((item) => item.changes?.some(changeBlocksPickup))
  const deliveryUnavailable =
    affectedLocationIds.size >= locationCount &&
    locations.every((item) => item.changes?.some(changeBlocksDelivery))
  const dcoOnly =
    affectedLocationIds.size >= locationCount &&
    locations.every((item) => item.changes?.some((change) => change.designers_choice_only))
  const temporaryHoursActive = changes.some((change) => change.temporary_hours)

  if (allLocationsClosed) {
    return {
      label: 'Store marked closed',
      variant: 'warning',
      description: 'Every active location is shown as closed on the website.',
    }
  }

  if (pickupUnavailable && deliveryUnavailable) {
    return {
      label: 'Pickup and delivery paused',
      variant: 'destructive',
      description: 'Website shoppers cannot schedule pickup or delivery for the affected dates.',
    }
  }

  if (pickupUnavailable) {
    return {
      label: 'Pickup pause',
      variant: 'warning',
      description: 'Website shoppers cannot schedule pickup for the affected dates.',
    }
  }

  if (deliveryUnavailable) {
    return {
      label: 'Delivery pause',
      variant: 'warning',
      description: 'Website shoppers cannot schedule delivery for the affected dates.',
    }
  }

  if (dcoOnly) {
    return {
      label: "Designer's Choice only",
      variant: 'warning',
      description:
        "Delivery / pickup dates are limited to orders containing Designer's Choice items.",
    }
  }

  if (temporaryHoursActive) {
    return {
      label: 'Temporary hours active',
      variant: 'secondary',
      description: 'Temporary pickup or delivery hours are currently overriding normal hours.',
    }
  }

  return {
    label: 'Limited availability',
    variant: 'warning',
    description: 'One or more locations has an active availability change.',
  }
}

function pluralizeAvailabilityChange(count: number): string {
  return count === 1 ? 'availability change' : 'availability changes'
}

export function formatUpcomingChange(change: AvailabilityChangeFields): string | null | undefined {
  const startsAt = change?.starts_at ? dayjs(change.starts_at) : null

  return startsAt?.isValid() ? startsAt.format('MMM D') : change?.start_date
}

export function getAvailabilityAlertTone(
  state: Pick<AvailabilityState, 'variant'>
): 'neutral' | 'pricing' {
  if (state.variant === 'muted') {
    return 'neutral'
  }

  return 'pricing'
}

export function getAvailabilityAlertTitle({
  activeChanges,
  upcomingChanges,
  state,
}: {
  /** The dashboard's summary changes, or the store status card's resolved occurrences. */
  activeChanges: readonly (ActiveAvailabilityChange | AvailabilityChangeOccurrence)[]
  upcomingChanges: readonly AvailabilityChangeOccurrence[]
  state: Pick<AvailabilityState, 'label' | 'variant'>
}): string {
  if (activeChanges.length > 1) {
    return `${formatNumber(activeChanges.length)} availability changes are active right now`
  }

  if (activeChanges.length === 1) {
    return 'Availability change active right now'
  }

  if (state.variant === 'muted') {
    return state.label
  }

  if (upcomingChanges.length > 0) {
    return `${formatNumber(upcomingChanges.length)} ${pluralizeAvailabilityChange(upcomingChanges.length)} scheduled`
  }

  return state.label
}

export function getAvailabilityDashboardDescription({
  hasActiveAvailabilityChanges,
  nextUpcomingLabel,
  state,
}: {
  hasActiveAvailabilityChanges: boolean
  nextUpcomingLabel: string | null | undefined
  state: Pick<AvailabilityState, 'description'>
}): string {
  if (hasActiveAvailabilityChanges) {
    return 'Review the current active availability changes and their schedules.'
  }

  if (nextUpcomingLabel) {
    return `Next scheduled availability change starts ${nextUpcomingLabel}.`
  }

  return state.description
}
